// src/components/programs/ProgramForm/Step1Sections/utils/DragDropContext.js

/**
 * Drag & Drop Context and Utilities
 * Handles drag operations between Products Panel and Days
 */

// Drag Types
export const DRAG_TYPES = {
    PRODUCT_NEW: 'product_new',        // From Products Panel
    PRODUCT_EXISTING: 'product_existing', // From Day to Day
    PRODUCT_REORDER: 'product_reorder',   // Within same Day
};

/**
 * Create drag data for new product from panel
 */
export const createProductDragData = (product) => {
    return {
        type: DRAG_TYPES.PRODUCT_NEW,
        product: product,
    };
};

/**
 * Create drag data for existing product instance
 */
export const createInstanceDragData = (product, dayIndex, isCopy = false) => {
    return {
        type: isCopy ? DRAG_TYPES.PRODUCT_EXISTING : DRAG_TYPES.PRODUCT_REORDER,
        product: product,
        sourceDayIndex: dayIndex,
        isCopy: isCopy,
    };
};

/**
 * Parse drag data from dataTransfer
 */
export const parseDragData = (dataTransfer) => {
    try {
        const data = dataTransfer.getData('application/json');
        return data ? JSON.parse(data) : null;
    } catch (error) {
        console.error('Error parsing drag data:', error);
        return null;
    }
};

/**
 * Set drag data to dataTransfer
 */
export const setDragData = (dataTransfer, data) => {
    try {
        dataTransfer.setData('application/json', JSON.stringify(data));
        dataTransfer.effectAllowed = data.isCopy ? 'copy' : 'move';
    } catch (error) {
        console.error('Error setting drag data:', error);
    }
};

/**
 * Create product instance from product data
 */
export const createProductInstance = (product, order = 1) => {
    const pricingCode = product.pricing_code || product.pricing_unit?.code || product.product_snapshot?.pricing_unit;

    // Default dynamic fields
    const dynamicFields = {};
    if (pricingCode === 'per_night') {
        // default nights to 0 so users can keep zero and proceed
        dynamicFields.nights = 0;
    }

    return {
        instance_id: `temp_${Date.now()}_${Math.random()}`,
        product_snapshot: {
            product_id: product.id,
            title: product.title,
            category: product.category,
            pricing_unit: product.pricing_code,
            original_price: product.price,
            original_cost_price: product.cost_price || 0,
            city: product.locations || null,
        },
        selected_supplier: product.suppliers?.[0] || null,
        selected_service: product.service || null,
        order: order,
        pricing: {
            price: product.price,
            cost_price: product.cost_price || 0,
            custom_price: null,
            is_price_edited: false,
            discount_type: "zero",
            discount_value: null,
        },
        dynamic_fields: dynamicFields,
        calculated: {
            final_price_per_unit: product.price,
            final_cost_per_unit: product.cost_price || 0,
            quantity: 0,
            total_price: 0,
            total_cost: 0,
        },
    };
};

/**
 * Clone existing product instance with new ID
 */
export const cloneProductInstance = (instance, order = 1) => {
    return {
        ...instance,
        instance_id: `temp_${Date.now()}_${Math.random()}`,
        order: order,
    };
};

/**
 * Check if product already exists in day
 */
export const isDuplicateProduct = (day, productId) => {
    return day.products.some(
        (p) => p.product_snapshot.product_id === productId
    );
};

/**
 * Get drop effect based on modifier keys
 */
export const getDropEffect = (event) => {
    // Ctrl (Windows/Linux) or Cmd (Mac) = Copy
    if (event.ctrlKey || event.metaKey) {
        return 'copy';
    }
    return 'move';
};

/**
 * Check if event is copy operation
 */
export const isCopyOperation = (event) => {
    return event.ctrlKey || event.metaKey;
};

const dragDropUtils = {
    DRAG_TYPES,
    createProductDragData,
    createInstanceDragData,
    parseDragData,
    setDragData,
    createProductInstance,
    cloneProductInstance,
    isDuplicateProduct,
    getDropEffect,
    isCopyOperation,
};

export default dragDropUtils;
